import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ISenderIdentity } from '../../../../interfaces/models/campaign';

// null when nothing usable is saved, so the caller falls back to its default.
function readSaved(key: string): string[] | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null');
    return Array.isArray(parsed) ? parsed.map(String) : null;
  } catch {
    return null;
  }
}

/*
  A sender filter that survives a refresh. Until the viewer picks something it opens
  on their own senders; after that their choice ("All" included) is what comes back.
  `ready` turns true once the starting value is settled, so nothing is fetched or
  filtered with a value that's about to change.
*/
export function useSavedSenderFilter(
  storageKey: string,
  identities: ISenderIdentity[],
  identitiesLoaded: boolean,
  currentUserId?: string,
): [string[], Dispatch<SetStateAction<string[]>>, boolean] {
  const [ids, setIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const key = `${storageKey}.${currentUserId ?? ''}`;

  useEffect(() => {
    if (ready || !identitiesLoaded) return;
    const saved = readSaved(key);
    if (saved) {
      const known = new Set(identities.map(i => String(i._id)));
      setIds(saved.filter(id => known.has(id)));
    } else {
      setIds(identities
        .filter(i => !!currentUserId && String(i.createdBy) === String(currentUserId))
        .map(i => String(i._id)));
    }
    setReady(true);
  }, [ready, identitiesLoaded, identities, currentUserId, key]);

  useEffect(() => {
    if (!ready) return;
    try { window.localStorage.setItem(key, JSON.stringify(ids)); } catch { /* storage unavailable */ }
  }, [ready, ids, key]);

  return [ids, setIds, ready];
}
